import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Table, Button, Space, Tag, Drawer, Form, Input, Select, Tabs, message, Typography, Popconfirm,
  Checkbox, Empty, AutoComplete, Upload, Tooltip, Modal, Dropdown, Radio,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  UploadOutlined, QuestionCircleOutlined,
} from '@ant-design/icons'
import * as apiDefinitionApi from '@/api/apiDefinitions'
import * as environmentApi from '@/api/environments'
import { post } from '@/utils/request'
import type { ApiImportResult } from '@/api/apiDefinitions'
import { useProjectStore } from '@/stores/project'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'
import type { ApiDefinition, ApiKVItem, Environment } from '@/types'

const { Title, Text } = Typography

const methodColor: Record<string, string> = {
  GET: 'blue',
  POST: 'success',
  PUT: 'orange',
  DELETE: 'red',
  PATCH: 'purple',
}

const methodPathStyle: Record<string, { bg: string; color: string }> = {
  GET: { bg: '#dbeafe', color: '#1d4ed8' },
  POST: { bg: '#dcfce7', color: '#166534' },
  PUT: { bg: '#ffedd5', color: '#c2410c' },
  DELETE: { bg: '#fee2e2', color: '#dc2626' },
  PATCH: { bg: '#ede9fe', color: '#7c3aed' },
}

const methodOptions = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((m) => ({ value: m, label: m }))

const commonHeaders = [
  'Content-Type', 'Authorization', 'Accept', 'User-Agent',
  'Cookie', 'Referer', 'Origin', 'Accept-Language', 'Accept-Encoding',
  'Cache-Control', 'Connection', 'Host', 'X-Requested-With', 'If-Modified-Since',
  'If-None-Match', 'X-CSRF-Token', 'X-Forwarded-For',
].map((h) => ({ value: h, label: h }))

const paramTypeOptions = ['string', 'integer', 'number', 'boolean', 'array'].map((t) => ({ value: t, label: t }))
const formDataTypeOptions = ['string', 'integer', 'number', 'boolean', 'array', 'json', 'file'].map((t) => ({ value: t, label: t }))
const urlencodedTypeOptions = ['string', 'integer', 'number', 'boolean', 'array'].map((t) => ({ value: t, label: t }))

const bodyTypeOptions = [
  { value: 'none', label: 'none' },
  { value: 'form-data', label: 'form-data' },
  { value: 'x-www-form-urlencoded', label: 'x-www-form-urlencoded' },
  { value: 'json', label: 'json' },
  { value: 'xml', label: 'xml' },
  { value: 'raw', label: 'raw' },
  { value: 'binary', label: 'binary' },
]

const assertionTypeOptions = [
  { value: 'status_code', label: '状态码' },
  { value: 'response_header', label: '响应头' },
  { value: 'response_body', label: '响应体' },
  { value: 'response_time', label: '响应时间' },
]

const assertionOperatorMap: Record<string, { value: string; label: string }[]> = {
  status_code: [
    { value: 'equals', label: '等于' },
    { value: 'not_equals', label: '不等于' },
  ],
  response_header: [
    { value: 'equals', label: '等于' },
    { value: 'not_equals', label: '不等于' },
    { value: 'contains', label: '包含' },
    { value: 'not_contains', label: '不包含' },
  ],
  response_body: [
    { value: 'equals', label: '等于' },
    { value: 'not_equals', label: '不等于' },
    { value: 'greater_than', label: '大于' },
    { value: 'greater_than_or_equal', label: '大于等于' },
    { value: 'less_than', label: '小于' },
    { value: 'less_than_or_equal', label: '小于等于' },
    { value: 'contains', label: '包含' },
    { value: 'not_contains', label: '不包含' },
    { value: 'starts_with', label: '以...开始' },
    { value: 'ends_with', label: '以...结束' },
    { value: 'is_empty', label: '为空' },
    { value: 'is_not_empty', label: '不为空' },
    { value: 'regex_match', label: '正则匹配' },
    { value: 'length_equals', label: '长度等于' },
    { value: 'length_greater_than', label: '长度大于' },
    { value: 'length_less_than', label: '长度小于' },
    { value: 'field_exists', label: '字段存在' },
    { value: 'field_not_exists', label: '字段不存在' },
    { value: 'is_null', label: '为null' },
    { value: 'is_not_null', label: '不为null' },
    { value: 'type_is', label: '类型为' },
  ],
  response_time: [
    { value: 'less_than', label: '小于' },
    { value: 'greater_than', label: '大于' },
  ],
}

interface ApiFormValues {
  name: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  path: string
  host: string
  headerEntries: ApiKVItem[]
  paramEntries: ApiKVItem[]
  restEntries: ApiKVItem[]
  body_type: 'none' | 'form-data' | 'x-www-form-urlencoded' | 'json' | 'xml' | 'raw' | 'binary'
  body: string
  bodyFormEntries: ApiKVItem[]
  description: string
}

interface AssertionRule {
  id: string
  type: 'status_code' | 'response_header' | 'response_body' | 'response_time'
  operator: string
  expected: string
  key: string
  expression: string
}

const kvArrayToJson = (entries: ApiKVItem[]): string => {
  const filtered = entries.filter((e) => e.key.trim() !== '')
  if (filtered.length === 0) return '[]'
  return JSON.stringify(filtered)
}

const jsonToKvArray = (jsonStr: string): ApiKVItem[] => {
  try {
    const parsed = JSON.parse(jsonStr || '[]')
    if (Array.isArray(parsed)) return parsed
    if (typeof parsed === 'object' && parsed !== null) {
      return Object.entries(parsed).map(([key, value]) => ({
        key,
        value: String(value),
        remark: '',
      }))
    }
    return []
  } catch {
    return []
  }
}

const buildDisplayUrl = (record: ApiDefinition): string => {
  let displayPath = record.path || ''
  // 去掉 path 中可能残留的 query string（旧数据/cURL 导入场景）
  const queryIndex = displayPath.indexOf('?')
  if (queryIndex !== -1) {
    displayPath = displayPath.slice(0, queryIndex)
  }

  // 替换 REST 参数占位符
  const restParams = jsonToKvArray(record.rest_params || '[]')
  restParams.filter((e) => e.key.trim()).forEach((e) => {
    displayPath = displayPath.replace(new RegExp(`{${e.key.trim()}}`, 'g'), e.value || '')
  })

  // 拼接 Query 参数
  const queryParams = jsonToKvArray(record.query_params || '[]')
  const queryParts = queryParams
    .filter((e) => e.key.trim())
    .map((e) => `${encodeURIComponent(e.key.trim())}=${encodeURIComponent(e.value || '')}`)
  if (queryParts.length > 0) {
    displayPath += '?' + queryParts.join('&')
  }

  return displayPath
}

// JSON 响应语法高亮渲染
const highlightJson = (text: string): React.ReactNode => {
  // 先尝试按 JSON 格式化后高亮
  try {
    JSON.parse(text) // 验证是否为合法 JSON
    // 使用正则分割并着色
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    const regex = /("(?:[^"\\]|\\.)*")\s*:/g
    let match
    while ((match = regex.exec(text)) !== null) {
      // 前面的内容作为普通值处理
      if (match.index > lastIndex) {
        const before = text.slice(lastIndex, match.index)
        parts.push(...colorizeFragment(before, `f${match.index}-`))
      }
      // key 用蓝色
      parts.push(<span key={`k${match.index}`} style={{ color: '#0369a1' }}>{match[1]}</span>)
      parts.push(<span key={`c${match.index}`} style={{ color: 'var(--au-text-faint)' }}>:</span>)
      lastIndex = match.index + match[0].length
    }
    // 剩余部分
    if (lastIndex < text.length) {
      parts.push(...colorizeFragment(text.slice(lastIndex), 'tail-'))
    }
    return <>{parts}</>
  } catch {
    // 非 JSON，使用 XML 风格高亮
    return colorizeXml(text)
  }
}

const colorizeFragment = (fragment: string, keyPrefix: string): React.ReactNode[] => {
  const result: React.ReactNode[] = []
  let remaining = fragment
  let key = 0
  while (remaining.length > 0) {
    const strMatch = remaining.match(/^("(?:[^"\\]|\\.)*")/)
    const numMatch = remaining.match(/^(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/)
    const boolMatch = remaining.match(/^(true|false)/)
    const nullMatch = remaining.match(/^(null)/)

    if (strMatch) {
      result.push(<span key={`${keyPrefix}s${key++}`} style={{ color: '#16a34a' }}>{strMatch[1]}</span>)
      remaining = remaining.slice(strMatch[0].length)
    } else if (numMatch) {
      result.push(<span key={`${keyPrefix}n${key++}`} style={{ color: '#d97706' }}>{numMatch[1]}</span>)
      remaining = remaining.slice(numMatch[0].length)
    } else if (boolMatch) {
      result.push(<span key={`${keyPrefix}b${key++}`} style={{ color: '#7c3aed', fontWeight: 600 }}>{boolMatch[1]}</span>)
      remaining = remaining.slice(boolMatch[0].length)
    } else if (nullMatch) {
      result.push(<span key={`${keyPrefix}l${key++}`} style={{ color: 'var(--au-text-faint)', fontStyle: 'italic' }}>{nullMatch[1]}</span>)
      remaining = remaining.slice(nullMatch[0].length)
    } else {
      const next = remaining[0]
      result.push(<span key={`${keyPrefix}p${key++}`}>{next}</span>)
      remaining = remaining.slice(1)
    }
  }
  return result
}

const colorizeXml = (text: string): React.ReactNode => {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  const regex = /(<\/?[\w:-]+)([^>]*)(\/?>)|([^<]+)/g
  let match
  let key = 0
  while ((match = regex.exec(text)) !== null) {
    if (match[4]) {
      parts.push(<span key={key++} style={{ color: 'var(--au-slate)' }}>{match[4]}</span>)
    } else {
      parts.push(<span key={key++} style={{ color: '#d946ef' }}>{'<'}{match[1]}</span>)
      if (match[2]) {
        // 属性高亮
        const attrRegex = /(\s+)([\w:-]+)(="([^"]*)")?/g
        let attrMatch
        let attrLast = 0
        while ((attrMatch = attrRegex.exec(match[2])) !== null) {
          if (attrMatch.index > attrLast) {
            parts.push(<span key={key++}>{match[2].slice(attrLast, attrMatch.index)}</span>)
          }
          parts.push(<span key={key++}>{attrMatch[1]}</span>)
          parts.push(<span key={key++} style={{ color: '#d97706' }}>{attrMatch[2]}</span>)
          if (attrMatch[3]) {
            parts.push(<span key={key++} style={{ color: 'var(--au-text-faint)' }}>=</span>)
            parts.push(<span key={key++} style={{ color: '#16a34a' }}>{attrMatch[4]}</span>)
          }
          attrLast = attrMatch.index + attrMatch[0].length
        }
        if (attrLast < match[2].length) {
          parts.push(<span key={key++}>{match[2].slice(attrLast)}</span>)
        }
      }
      parts.push(<span key={key++} style={{ color: '#d946ef' }}>{match[3]}</span>)
    }
  }
  return <>{parts}</>
}

export default function ApiTest() {
  const { isDark } = useThemeStore()
  const [apis, setApis] = useState<ApiDefinition[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keyword, setKeyword] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [methodFilter, setMethodFilter] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingApi, setEditingApi] = useState<ApiDefinition | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('headers')
  const [form] = Form.useForm<ApiFormValues>()

  // 保存弹框
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [saveDescription, setSaveDescription] = useState('')
  const [saveStatus, setSaveStatus] = useState('active')
  const [saveNameError, setSaveNameError] = useState('')

  // 批量导入弹框
  const [batchImportOpen, setBatchImportOpen] = useState(false)
  const [importType, setImportType] = useState('curl')
  const [curlText, setCurlText] = useState('')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<ApiImportResult | null>(null)

  // 运行环境
  const [environments, setEnvironments] = useState<Environment[]>([])
  const [envLoading, setEnvLoading] = useState(false)

  // 调试响应
  const [debugging, setDebugging] = useState(false)
  const [responseResult, setResponseResult] = useState<{ status: number; body: string; headers: Record<string, string>; elapsed_ms: number } | null>(null)
  const [responseTab, setResponseTab] = useState<'body' | 'headers' | 'assert'>('body')
  const [assertionRules, setAssertionRules] = useState<AssertionRule[]>([])
  const [assertionResults, setAssertionResults] = useState<{ id: string; passed: boolean; message: string }[]>([])
  const assertionIdRef = useRef(0)

  const addAssertionRule = (type: AssertionRule['type']) => {
    const id = `assert_${assertionIdRef.current++}`
    const operators = assertionOperatorMap[type] || []
    setAssertionRules((prev) => [...prev, { id, type, operator: operators[0]?.value || '', expected: '', key: '', expression: '' }])
  }

  const removeAssertionRule = (id: string) => {
    setAssertionRules((prev) => prev.filter((r) => r.id !== id))
  }

  const updateAssertionRule = (id: string, updates: Partial<AssertionRule>) => {
    setAssertionRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)))
  }

  const { currentProject } = useProjectStore()
  const { user } = useAuthStore()
  const projectId = currentProject?.id || 0
  const pageRef = useRef<HTMLDivElement>(null)

  const loadEnvironments = async (currentHost?: string) => {
    if (!projectId) return
    setEnvLoading(true)
    try {
      const res = await environmentApi.listEnvironments({ project_id: projectId, page: 1, page_size: 100 })
      setEnvironments(res.data)
      // 仅当接口未保存 host 时，默认加载项目环境列表第一条
      if (!currentHost && res.data.length > 0) {
        form.setFieldValue('host', res.data[0].url)
      }
    } catch { /* 忽略 */ } finally {
      setEnvLoading(false)
    }
  }

  const fetchList = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await apiDefinitionApi.listApiDefinitions({
        project_id: projectId,
        page,
        page_size: pageSize,
        keyword: searchKeyword || undefined,
        method: methodFilter || undefined,
      })
      setApis(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [projectId, page, pageSize, searchKeyword, methodFilter])

  const handleSearch = () => {
    const nextKeyword = keyword.trim()
    if (page === 1 && searchKeyword === nextKeyword) {
      fetchList()
      return
    }
    setSearchKeyword(nextKeyword)
    setPage(1)
  }

  const handleBatchImport = () => {
    setBatchImportOpen(true)
    setImportResult(null)
    setCurlText('')
    setImportFile(null)
  }

  const handleImportFileChange = (info: any) => {
    const file = info?.file?.originFileObj || info?.file
    if (file) {
      setImportFile(file as File)
    } else {
      setImportFile(null)
    }
  }

  const handleBatchImportSubmit = async () => {
    if (!projectId) {
      message.error('请先选择项目')
      return
    }

    let content = ''
    if (importType === 'curl') {
      content = curlText.trim()
      if (!content) {
        message.error('请输入 cURL 命令')
        return
      }
    } else {
      if (!importFile) {
        message.error('请选择要导入的文件')
        return
      }
      content = await readFileAsText(importFile)
    }

    setImporting(true)
    try {
      const res = await apiDefinitionApi.importApiDefinitions({
        project_id: projectId,
        import_type: importType,
        content,
      })
      setImportResult(res)
      message.success(`导入完成：成功 ${res.success_count} 条，失败 ${res.fail_count} 条`)
      fetchList()
    } catch {
      message.error('导入失败，请重试')
    } finally {
      setImporting(false)
    }
  }

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => reject(new Error('文件读取失败'))
      reader.readAsText(file)
    })
  }

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const resetForm = () => {
    form.setFieldsValue({
      name: '新增请求',
      method: 'GET',
      path: '',
      host: '',
      headerEntries: [],
      paramEntries: [],
      restEntries: [],
      body_type: 'none',
      body: '',
      bodyFormEntries: [],
      description: '',
    })
  }

  const handleCreate = () => {
    setEditingApi(null)
    setResponseResult(null)
    setAssertionRules([])
    setAssertionResults([])
    setActiveTab('headers')
    setBodyType('none')
    form.resetFields()
    resetForm()
    setDrawerOpen(true)
    loadEnvironments()
  }

  const handleEdit = (record: ApiDefinition) => {
    setEditingApi(record)
    setResponseResult(null)
    // 从数据库加载断言规则
    try {
      const saved = JSON.parse(record.assertions || '[]')
      if (Array.isArray(saved)) {
        setAssertionRules(saved)
      } else {
        setAssertionRules([])
      }
    } catch {
      setAssertionRules([])
    }
    setAssertionResults([])
    setActiveTab('headers')
    const bt = record.body_type || 'none'
    setBodyType(bt)
    let bodyFormEntries: any[] = []
    if (bt === 'form-data' || bt === 'x-www-form-urlencoded') {
      try {
        const parsed = JSON.parse(record.body || '[]')
        if (Array.isArray(parsed)) {
          bodyFormEntries = parsed.map((e: any) => ({
            key: e.key || '',
            value: ['string', 'integer', 'number', 'boolean', 'array', 'json', 'file'].includes(e.type) ? e.type : 'string',
            remark: e.value || e.remark || '',
          }))
        }
      } catch {
        // body 不是 JSON 数组时忽略
      }
    }

    form.setFieldsValue({
      name: record.name,
      method: record.method,
      path: record.path,
      host: record.host || '',
      headerEntries: jsonToKvArray(record.headers),
      paramEntries: jsonToKvArray(record.query_params),
      restEntries: jsonToKvArray(record.rest_params || '[]'),
      body_type: bt,
      body: record.body || '',
      bodyFormEntries,
      description: record.description || '',
    })
    setDrawerOpen(true)
    loadEnvironments(record.host || undefined)
  }

  const handleDelete = async (id: number) => {
    try {
      await apiDefinitionApi.deleteApiDefinition(id, projectId)
      message.success('删除成功')
      fetchList()
    } catch {
      // 错误已在拦截器处理
    }
  }

  const handleDebug = async () => {
    try {
      const values = await form.validateFields()
      // Tabs 懒渲染：未点击过的页签内字段未注册，validateFields 拿不到其值
      // （Query/REST/请求体页签均受影响）。getFieldsValue(true) 直读 form store，
      // 包含 setFieldsValue 写入但尚未渲染的字段值，彻底避免调试丢参
      const tabValues = form.getFieldsValue(true) as ApiFormValues
      setDebugging(true)
      setResponseResult(null)
      const envUrl = values.host || ''
      let path = values.path || ''
      // REST 参数：替换路径中的 {param} 占位符
      if (tabValues.restEntries) {
        tabValues.restEntries.filter(e => e.key.trim()).forEach(e => {
          path = path.replace(`{${e.key.trim()}}`, e.value || '')
        })
      }
      const fullUrl = envUrl ? envUrl.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '') : path

      const headers: Record<string, string> = {}
      if (tabValues.headerEntries) {
        tabValues.headerEntries.filter(e => e.key.trim()).forEach(e => { headers[e.key.trim()] = e.value || '' })
      }

      const queryParams: Record<string, string> = {}
      if (tabValues.paramEntries) {
        tabValues.paramEntries.filter(e => e.key.trim()).forEach(e => { queryParams[e.key.trim()] = e.value || '' })
      }

      let body: string | FormData | null = null
      let effectiveBodyType = tabValues.body_type || 'none'
      const bodyFormEntries = tabValues.bodyFormEntries || []
      const bodyValue = tabValues.body || ''
      if (effectiveBodyType === 'form-data' && bodyFormEntries.length) {
        const formData: Record<string, any> = {}
        bodyFormEntries.forEach((entry: any) => {
          if (entry.key) {
            formData[entry.key] = entry.remark || ''
          }
        })
        body = JSON.stringify(formData)
      } else if (effectiveBodyType === 'x-www-form-urlencoded' && bodyFormEntries.length) {
        const params = new URLSearchParams()
        bodyFormEntries.forEach((entry: any) => {
          if (entry.key) {
            params.append(entry.key, entry.remark || '')
          }
        })
        body = params.toString()
      } else if (bodyValue.trim()) {
        body = bodyValue
        if (effectiveBodyType === 'none') {
          try {
            JSON.parse(bodyValue)
            effectiveBodyType = 'json'
          } catch {
            effectiveBodyType = 'raw'
          }
        }
      }

      const res = await post<{ status: number; body: string; headers: Record<string, string>; elapsed_ms: number }>('/api-definitions/debug', {
        method: values.method,
        url: fullUrl,
        headers,
        query_params: queryParams,
        body_type: effectiveBodyType,
        body,
      })
      // 默认格式化响应体
      try {
        res.body = JSON.stringify(JSON.parse(res.body), null, 2)
      } catch {
        // 非 JSON 则保持原样
      }
      setResponseResult(res)
      // 评估断言规则
      evaluateAssertions(assertionRules, res)
      // 如果有断言规则，自动切换到断言结果Tab
      if (assertionRules.length > 0) {
        setResponseTab('assert')
      }
    } catch (e) {
      console.error('[ApiTest] 调试失败:', e)
    } finally {
      setDebugging(false)
    }
  }

  const handleSaveClick = () => {
    const values = form.getFieldsValue()
    const path = (values.path || '').trim()

    // 新增时校验必填项：URL
    if (!editingApi && !path) {
      message.error('请输入URL')
      return
    }

    // 打开保存弹框，预填接口名称、描述和状态
    setSaveName(values.name || '新增请求')
    setSaveDescription(editingApi?.description || values.description || '')
    setSaveStatus(editingApi?.status || 'active')
    setSaveNameError('')
    setSaveModalOpen(true)
  }

  const handleModalConfirm = () => {
    const trimmed = saveName.trim()
    if (!trimmed) {
      setSaveNameError('接口名称不能为空')
      return
    }
    setSaveModalOpen(false)
    form.setFieldValue('name', trimmed)
    form.setFieldValue('description', saveDescription)
    // getFieldsValue(true)：含未渲染页签（Query/REST/请求体）中的缓存值，避免保存丢参
    const values = form.getFieldsValue(true)
    doSubmit({ ...values, name: trimmed, description: saveDescription, status: saveStatus } as ApiFormValues)
  }

  const doSubmit = async (values: ApiFormValues) => {
    setSubmitting(true)
    try {
      const status: 'active' | 'deprecated' = ((values as { status?: string }).status || 'active') as 'active' | 'deprecated'
      // 根据请求体类型序列化 body：form-data / x-www-form-urlencoded 从 bodyFormEntries 生成
      let bodyToSave = values.body || ''
      const effectiveBodyType = values.body_type || 'none'
      const bodyFormEntries = values.bodyFormEntries || []
      if ((effectiveBodyType === 'form-data' || effectiveBodyType === 'x-www-form-urlencoded') && bodyFormEntries.length) {
        bodyToSave = JSON.stringify(
          bodyFormEntries
            .filter((e) => e.key && e.key.trim())
            .map((e) => ({ key: e.key, type: e.value || 'string', value: e.remark || '' })),
        )
      }
      const payload = {
        name: values.name,
        method: values.method,
        path: values.path,
        host: values.host || '',
        headers: kvArrayToJson(values.headerEntries || []),
        query_params: kvArrayToJson(values.paramEntries || []),
        rest_params: kvArrayToJson(values.restEntries || []),
        body_type: effectiveBodyType,
        body: bodyToSave,
        description: values.description || '',
        assertions: JSON.stringify(assertionRules),
        status,
      }
      if (editingApi) {
        await apiDefinitionApi.updateApiDefinition(editingApi.id, payload, projectId)
        message.success('更新成功')
      } else {
        await apiDefinitionApi.createApiDefinition({
          ...payload,
          project_id: projectId,
          creator_id: user?.id || 1,
        })
        message.success('创建成功')
      }
      setDrawerOpen(false)
      fetchList()
    } catch {
      // 表单校验或接口错误
    } finally {
      setSubmitting(false)
    }
  }

  const resolveJsonPath = (obj: Record<string, unknown>, path: string): unknown => {
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean)
    let current: Record<string, unknown> | unknown = obj
    for (const part of parts) {
      if (current == null) return undefined
      current = (current as Record<string, unknown>)[part]
    }
    return current
  }

  const evaluateAssertions = (rules: AssertionRule[], res: { status: number; body: string; headers: Record<string, string>; elapsed_ms: number }) => {
    if (!rules || rules.length === 0) {
      setAssertionResults([])
      return
    }
    const results = rules.map((rule) => {
      let passed = false
      let msg = ''
      const expected = rule.expected || ''
      switch (rule.type) {
        case 'status_code': {
          const statusStr = String(res.status)
          if (rule.operator === 'equals') {
            passed = statusStr === expected
            msg = passed ? `状态码等于 ${expected}` : `状态码应为 ${expected}，实际为 ${res.status}`
          } else if (rule.operator === 'not_equals') {
            passed = statusStr !== expected
            msg = passed ? `状态码不等于 ${expected}` : `状态码不应为 ${expected}，实际为 ${res.status}`
          }
          break
        }
        case 'response_header': {
          const headerVal = (res.headers[rule.key] || res.headers[rule.key.toLowerCase()] || '').toString()
          if (rule.operator === 'equals') {
            passed = headerVal === expected
            msg = passed ? `响应头 ${rule.key} 等于 ${expected}` : `响应头 ${rule.key} 应为 ${expected}，实际为 ${headerVal || '(空)'}`
          } else if (rule.operator === 'not_equals') {
            passed = headerVal !== expected
            msg = passed ? `响应头 ${rule.key} 不等于 ${expected}` : `响应头 ${rule.key} 不应为 ${expected}，实际为 ${headerVal || '(空)'}`
          } else if (rule.operator === 'contains') {
            passed = headerVal.includes(expected)
            msg = passed ? `响应头 ${rule.key} 包含 ${expected}` : `响应头 ${rule.key} 应包含 ${expected}，实际为 ${headerVal || '(空)'}`
          } else if (rule.operator === 'not_contains') {
            passed = !headerVal.includes(expected)
            msg = passed ? `响应头 ${rule.key} 不包含 ${expected}` : `响应头 ${rule.key} 不应包含 ${expected}，实际为 ${headerVal || '(空)'}`
          }
          break
        }
        case 'response_body': {
          const rawBody = res.body || ''
          let bodyStr = rawBody
          let extractedRaw: unknown = undefined
          // 如有表达式，尝试提取 JSON 路径值
          if (rule.expression) {
            try {
              const parsed = JSON.parse(rawBody)
              extractedRaw = resolveJsonPath(parsed, rule.expression)
              bodyStr = extractedRaw !== undefined ? String(extractedRaw) : rawBody
            } catch {
              // JSON 解析失败则使用原始 body
            }
          }
          const op = rule.operator
          // 需要原始值的操作
          if (op === 'is_null') {
            passed = !rule.expression || extractedRaw === null
            msg = passed ? '值为null' : '值不为null'
          } else if (op === 'is_not_null') {
            passed = rule.expression ? extractedRaw !== null && extractedRaw !== undefined : bodyStr !== null
            msg = passed ? '值不为null' : '值为null或不存在'
          } else if (op === 'field_exists') {
            passed = extractedRaw !== undefined
            msg = passed ? `字段 "${rule.expression}" 存在` : `字段 "${rule.expression}" 不存在`
          } else if (op === 'field_not_exists') {
            passed = extractedRaw === undefined
            msg = passed ? `字段 "${rule.expression}" 不存在` : `字段 "${rule.expression}" 存在`
          } else if (op === 'type_is') {
            const actualType = extractedRaw === null ? 'null' : extractedRaw === undefined ? 'undefined' : Array.isArray(extractedRaw) ? 'array' : typeof extractedRaw
            passed = actualType === expected.toLowerCase()
            msg = passed ? `类型为 ${expected}` : `类型应为 ${expected}，实际为 ${actualType}`
          }
          // 空值判断
          else if (op === 'is_empty') {
            passed = bodyStr === '' || bodyStr === 'null' || bodyStr === 'undefined'
            msg = passed ? '值为空' : `值不为空: ${bodyStr.length > 50 ? bodyStr.slice(0, 50) + '...' : bodyStr}`
          } else if (op === 'is_not_empty') {
            passed = bodyStr !== '' && bodyStr !== 'null' && bodyStr !== 'undefined'
            msg = passed ? '值不为空' : '值为空'
          }
          // 正则匹配
          else if (op === 'regex_match') {
            try {
              passed = new RegExp(expected).test(bodyStr)
              msg = passed ? `正则 "/${expected}/" 匹配成功` : `正则 "/${expected}/" 不匹配`
            } catch {
              passed = false
              msg = `正则表达式 "/${expected}/" 无效`
            }
          }
          // 字符串操作
          else if (op === 'contains') {
            passed = bodyStr.includes(expected)
            msg = passed ? `包含 "${expected}"` : `应包含 "${expected}"`
          } else if (op === 'not_contains') {
            passed = !bodyStr.includes(expected)
            msg = passed ? `不包含 "${expected}"` : `不应包含 "${expected}"`
          } else if (op === 'starts_with') {
            passed = bodyStr.startsWith(expected)
            msg = passed ? `以 "${expected}" 开始` : `应以 "${expected}" 开始`
          } else if (op === 'ends_with') {
            passed = bodyStr.endsWith(expected)
            msg = passed ? `以 "${expected}" 结束` : `应以 "${expected}" 结束`
          }
          // 长度比较
          else if (op === 'length_equals') {
            const len = bodyStr.length
            const expLen = parseInt(expected, 10)
            passed = len === expLen
            msg = passed ? `长度等于 ${expLen}` : `长度应为 ${expLen}，实际为 ${len}`
          } else if (op === 'length_greater_than') {
            const len = bodyStr.length
            const expLen = parseInt(expected, 10)
            passed = len > expLen
            msg = passed ? `长度大于 ${expLen}` : `长度应大于 ${expLen}，实际为 ${len}`
          } else if (op === 'length_less_than') {
            const len = bodyStr.length
            const expLen = parseInt(expected, 10)
            passed = len < expLen
            msg = passed ? `长度小于 ${expLen}` : `长度应小于 ${expLen}，实际为 ${len}`
          }
          // 数值比较
          else if (op === 'equals') {
            passed = String(bodyStr) === String(expected)
            msg = passed ? `等于 ${expected}` : `应为 ${expected}，实际为 ${bodyStr}`
          } else if (op === 'not_equals') {
            passed = String(bodyStr) !== String(expected)
            msg = passed ? `不等于 ${expected}` : `不应等于 ${expected}`
          } else {
            const numVal = parseFloat(bodyStr)
            const numExp = parseFloat(expected)
            if (op === 'greater_than') {
              passed = !isNaN(numVal) && numVal > numExp
              msg = passed ? `${numVal} > ${numExp}` : `${numVal} 应大于 ${numExp}`
            } else if (op === 'greater_than_or_equal') {
              passed = !isNaN(numVal) && numVal >= numExp
              msg = passed ? `${numVal} >= ${numExp}` : `${numVal} 应大于等于 ${numExp}`
            } else if (op === 'less_than') {
              passed = !isNaN(numVal) && numVal < numExp
              msg = passed ? `${numVal} < ${numExp}` : `${numVal} 应小于 ${numExp}`
            } else if (op === 'less_than_or_equal') {
              passed = !isNaN(numVal) && numVal <= numExp
              msg = passed ? `${numVal} <= ${numExp}` : `${numVal} 应小于等于 ${numExp}`
            } else {
              msg = `未知操作符: ${op}`
            }
          }
          break
        }
        case 'response_time': {
          const time = res.elapsed_ms
          if (rule.operator === 'less_than') {
            const limit = parseFloat(expected)
            passed = time < limit
            msg = passed ? `响应时间 ${time}ms < ${limit}ms` : `响应时间 ${time}ms 应小于 ${limit}ms`
          } else if (rule.operator === 'greater_than') {
            const limit = parseFloat(expected)
            passed = time > limit
            msg = passed ? `响应时间 ${time}ms > ${limit}ms` : `响应时间 ${time}ms 应大于 ${limit}ms`
          }
          break
        }
      }
      return { id: rule.id, passed, message: msg }
    })
    setAssertionResults(results)
  }

  const handleFormatResponse = () => {
    if (responseResult) {
      try {
        const parsed = JSON.parse(responseResult.body)
        setResponseResult({ ...responseResult, body: JSON.stringify(parsed, null, 2) })
        message.success('已格式化')
      } catch {
        message.warning('响应内容不是有效的 JSON 格式')
      }
    }
  }

  const columns: ColumnsType<ApiDefinition> = [
    {
      title: '请求类型',
      dataIndex: 'method',
      width: 90,
      render: (v: string) => (
        <Tag color={methodColor[v]} style={{ minWidth: 60, textAlign: 'center' }}>
          {v}
        </Tag>
      ),
    },
    { title: '接口名称', dataIndex: 'name', width: 160 },
    {
      title: '接口描述',
      dataIndex: 'description',
      width: 180,
      ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: '请求URL',
      dataIndex: 'path',
      render: (_v: string, record: ApiDefinition) => {
        const style = methodPathStyle[record.method] || { bg: 'var(--au-row-alt)', color: 'var(--au-slate)' }
        const fullUrl = buildDisplayUrl(record)
        return (
          <code style={{ color: style.color, background: style.bg, padding: '3px 8px', borderRadius: 4, fontSize: 14.5, fontWeight: 700 }}>
            {fullUrl}
          </code>
        )
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>状态</div>,
      dataIndex: 'status',
      width: 90,
      render: (v: string) => <Tag color={v === 'active' ? 'success' : 'error'}>{v === 'active' ? '启用' : '废弃'}</Tag>,
    },
    {
      title: '操作',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm
            title="确定删除此接口？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const renderKvTable = (
    name: string,
    placeholders: { key: string; value: string; remark: string },
    keySuggestions?: { value: string; label: string }[],
  ) => (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 1fr 1.2fr 60px',
            gap: 0,
            padding: '8px 12px',
            background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
            borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
            color: isDark ? 'var(--text-light)' : 'var(--au-slate)',
            fontWeight: 500,
            fontSize: 13,
            alignItems: 'center',
          }}>
            <Checkbox disabled defaultChecked />
            <span>参数名称</span>
            <span>参数值</span>
            <span>描述</span>
            <Button type="link" size="small" onClick={() => add({ key: '', value: '', remark: '' })} style={{ padding: 0 }}>
              添加
            </Button>
          </div>

          {fields.length === 0 && (
            <div style={{ padding: '24px 0', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
              暂无数据，点击右上角"添加"新增
            </div>
          )}

          {fields.map(({ key, name: fieldName, ...restField }, index) => (
            <div
              key={key}
              style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 1fr 1.2fr 60px',
                gap: 0,
                padding: '4px 12px',
                alignItems: 'center',
                borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
                background: isDark
                  ? (index % 2 === 0 ? 'var(--bg)' : 'var(--card)')
                  : (index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'),
              }}
            >
              <Checkbox defaultChecked />
              <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0, marginRight: 8 }}>
                {keySuggestions ? (
                  <AutoComplete
                    size="small"
                    options={keySuggestions}
                    filterOption={(inputValue, option) =>
                      option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    placeholder={placeholders.key}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <Input size="small" placeholder={placeholders.key} bordered={false} />
                )}
              </Form.Item>
              <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0, marginRight: 8 }}>
                <Input size="small" placeholder={placeholders.value} bordered={false} style={{ background: isDark ? 'var(--bg)' : undefined, color: isDark ? 'var(--text)' : undefined }} />
              </Form.Item>
              <Form.Item {...restField} name={[fieldName, 'remark']} style={{ marginBottom: 0, marginRight: 8 }}>
                <Input size="small" placeholder={placeholders.remark} bordered={false} style={{ background: isDark ? 'var(--bg)' : undefined, color: isDark ? 'var(--text)' : undefined }} />
              </Form.Item>
              <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
                删除
              </Button>
            </div>
          ))}
        </div>
      )}
    </Form.List>
  )

  // 当前方法
  const currentMethod = Form.useWatch('method', form) || 'GET'
  const [bodyType, setBodyType] = useState<string>('none')

  return (
    <div ref={pageRef} className="aurora-page aurora-page--fit api-page-aurora" style={{ position: 'relative' }}>
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>接口列表</Title>
        <Space>
          <Button onClick={fetchList}>刷新</Button>
          <Button
            type="primary"
            onClick={handleCreate}
            disabled={!projectId}
            style={{
              background: 'var(--au-primary)',
              border: 'none',
            }}
          >
            新增接口
          </Button>
          <Button onClick={handleBatchImport} disabled={!projectId}>批量导入</Button>
        </Space>
      </div>

      <div className="search-bar">
        <Space>
          <Select
            placeholder="请求方法"
            allowClear
            style={{ width: 120 }}
            value={methodFilter || undefined}
            onChange={(v) => { setMethodFilter(v || ''); setPage(1) }}
            options={methodOptions}
          />
          <Space.Compact>
            <Input
              placeholder="搜索接口名称或路径"
              allowClear
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={handleSearch}
              style={{ minWidth: 200 }}
            />
            <Button
              type="primary"
              onClick={handleSearch}
              style={{ background: isDark ? 'var(--au-primary)' : undefined, borderColor: isDark ? 'var(--au-primary)' : undefined, color: isDark ? '#fff' : undefined }}
            >
              查询
            </Button>
          </Space.Compact>
        </Space>
      </div>

      <div className="content-card">
        <Table
          columns={columns}
          dataSource={apis}
          rowKey="id"
          size="middle"
          loading={loading}
          scroll={{ y: '100%' }}
          pagination={{
            current: page,
            total,
            pageSize,
            showSizeChanger: true,
            showTotal: (t: number) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) },
          }}
        />
      </div>

      <Drawer
        title={null}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="right"
        width="70%"
        rootClassName="aurora-edit-drawer"
        rootStyle={{ top: 56, height: 'calc(100vh - 56px)' }}
        maskStyle={{ top: 56, height: 'calc(100vh - 56px)', background: 'transparent' }}
        bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column' }}
        headerStyle={{ display: 'none' }}
      >
        <Form form={form} layout="vertical" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Form.Item name="body_type" hidden>
            <Input />
          </Form.Item>
          {/* 顶部：方法标签 + 标题 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 24px',
            borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
            background: isDark ? 'var(--card)' : '#ffffff',
            flexShrink: 0,
            gap: 12,
          }}>
            <Tag
              color={methodColor[currentMethod as keyof typeof methodColor] || 'default'}
              style={{
                fontSize: 14,
                fontWeight: 600,
                padding: '2px 10px',
                margin: 0,
              }}
            >
              {currentMethod}
            </Tag>
            <Form.Item name="name" style={{ margin: 0, flex: 1 }}>
              <Input
                bordered={false}
                style={{ fontSize: 16, fontWeight: 600 }}
              />
            </Form.Item>
          </div>

          {/* 请求行：方法 + 环境 + URL + 调试 + 保存 */}
          <div className="aurora-urlbar" style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 24px',
            gap: 0,
            borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
            background: isDark ? 'var(--card)' : '#ffffff',
            flexShrink: 0,
          }}>
            <div style={{
              width: 110,
              border: '1px solid var(--au-border)',
              borderRadius: '4px 0 0 4px',
            }}>
              <Form.Item name="method" style={{ margin: 0 }}>
                <Select
                  options={methodOptions.map(m => ({ ...m, color: methodColor[m.value as keyof typeof methodColor] }))}
                  optionRender={(opt) => (
                    <span style={{ color: (opt.data as any).color, fontWeight: 600 }}>
                      {opt.label}
                    </span>
                  )}
                  labelRender={(props) => (
                    <span style={{ color: methodColor[props.value as keyof typeof methodColor] || '#06b6d4', fontWeight: 600 }}>
                      {props.label}
                    </span>
                  )}
                  bordered={false}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
            <div style={{
              width: 220,
              border: '1px solid var(--au-border)',
              borderRight: 'none',
            }}>
              <Form.Item name="host" style={{ margin: 0 }}>
                <Select
                  placeholder="环境"
                  allowClear
                  loading={envLoading}
                  bordered={false}
                  style={{ width: '100%' }}
                  options={(() => {
                    const envUrls = environments.map((env) => ({ value: env.url, label: env.url }))
                    // 合并接口已保存的 host 并按 value 去重
                    const currentHost = form.getFieldValue('host')
                    if (currentHost && !envUrls.some((o) => o.value === currentHost)) {
                      return [{ value: currentHost, label: currentHost }, ...envUrls]
                    }
                    return envUrls
                  })()}
                />
              </Form.Item>
            </div>
            <Form.Item name="path" style={{ margin: 0, flex: 1 }}>
              <Input
                placeholder="请输入URL"
                style={{ borderRadius: '0 6px 6px 0', borderLeft: 'none' }}
              />
            </Form.Item>
            <Button
              type="primary"
              loading={debugging}
              onClick={handleDebug}
              className="pill-btn"
              style={{
                marginLeft: 10,
                background: 'var(--au-primary)',
                border: 'none',
              }}
            >
              调试
            </Button>
            <Button
              className="pill-btn"
              style={{
                marginLeft: 8,
              }}
              onClick={handleSaveClick}
              loading={submitting}
            >
              保存
            </Button>
          </div>

          {/* Tabs 区域：上半部分 */}
          <div style={{
            flex: '1 1 200px',
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              tabBarStyle={{ paddingLeft: 24 }}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
              items={[
                {
                  key: 'headers',
                  label: '请求头',
                  children: (
                    <div className="thin-scrollbar" style={{ padding: '0 24px 70px', maxHeight: 'min(340px, 38vh)', overflowY: 'auto' }}>
                      {renderKvTable('headerEntries', { key: 'Header Key', value: 'Header Value', remark: '描述' }, commonHeaders)}
                    </div>
                  ),
                },
                {
                  key: 'body',
                  label: '请求体',
                  children: (
                    <div style={{ padding: '0 24px 16px' }}>
                      <div style={{ marginBottom: 12 }}>
                        {bodyTypeOptions.map((opt) => (
                          <Text
                            key={opt.value}
                            onClick={() => { form.setFieldsValue({ body_type: opt.value as any }); setBodyType(opt.value) }}
                            style={{
                              cursor: 'pointer',
                              padding: '4px 12px',
                              marginRight: 8,
                              borderRadius: 4,
                              fontSize: 13,
                              color: bodyType === opt.value ? 'var(--au-primary)' : (isDark ? '#94A3B8' : 'var(--au-slate-muted)'),
                              background: bodyType === opt.value ? (isDark ? 'rgba(255,255,255,0.12)' : 'var(--au-primary-50)') : (isDark ? 'rgba(255,255,255,0.06)' : 'var(--au-row-alt)'),
                              border: bodyType === opt.value ? '1px solid var(--au-primary)' : (isDark ? '1px solid var(--border)' : '1px solid transparent'),
                            }}
                          >
                            {opt.label}
                          </Text>
                        ))}
                      </div>
                      {/* none: 无 Body */}
                      {bodyType === 'none' && (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--au-slate-muted)', fontSize: 14 }}>
                          该请求没有 Body
                        </div>
                      )}
                      {/* form-data: 表单表格 */}
                      {bodyType === 'form-data' && (
                        <Form.List name="bodyFormEntries">
                          {(fields, { add, remove }) => (
                            <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '40px 1fr 100px 1fr 60px',
                                gap: 0,
                                padding: '8px 12px',
                                background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                                borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                                color: isDark ? 'var(--text-light)' : 'var(--au-slate)',
                                fontWeight: 500,
                                fontSize: 13,
                                alignItems: 'center',
                              }}>
                                <Checkbox disabled defaultChecked />
                                <span>参数名称</span>
                                <span>类型</span>
                                <span>参数值</span>
                                <Button type="link" size="small" onClick={() => add({ key: '', value: 'string', remark: '' })} style={{ padding: 0 }}>
                                  添加
                                </Button>
                              </div>

                              {fields.length === 0 && (
                                <div style={{ padding: '24px 0', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
                                  暂无数据，点击右上角"添加"新增
                                </div>
                              )}

                              {fields.map(({ key, name: fieldName, ...restField }, index) => (
                                <div
                                  key={key}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '40px 1fr 100px 1fr 60px',
                                    gap: 0,
                                    padding: '4px 12px',
                                    alignItems: 'center',
                                    borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
                                    background: isDark
                                      ? (index % 2 === 0 ? 'var(--bg)' : 'var(--card)')
                                      : (index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'),
                                  }}
                                >
                                  <Checkbox defaultChecked />
                                  <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0, marginRight: 8 }}>
                                    <Input size="small" placeholder="参数名称" bordered={false} />
                                  </Form.Item>
                                  <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0, marginRight: 8 }}>
                                    <Select
                                      size="small"
                                      bordered={false}
                                      options={formDataTypeOptions}
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                  <Form.Item {...restField} name={[fieldName, 'remark']} style={{ marginBottom: 0, marginRight: 8 }}>
                                    <Input size="small" placeholder="参数值" bordered={false} />
                                  </Form.Item>
                                  <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
                                    删除
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </Form.List>
                      )}
                      {/* x-www-form-urlencoded: 表单表格 */}
                      {bodyType === 'x-www-form-urlencoded' && (
                        <Form.List name="bodyFormEntries">
                          {(fields, { add, remove }) => (
                            <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '40px 1fr 100px 1fr 60px',
                                gap: 0,
                                padding: '8px 12px',
                                background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                                borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                                color: isDark ? 'var(--text-light)' : 'var(--au-slate)',
                                fontWeight: 500,
                                fontSize: 13,
                                alignItems: 'center',
                              }}>
                                <Checkbox disabled defaultChecked />
                                <span>参数名称</span>
                                <span>类型</span>
                                <span>参数值</span>
                                <Button type="link" size="small" onClick={() => add({ key: '', value: 'string', remark: '' })} style={{ padding: 0 }}>
                                  添加
                                </Button>
                              </div>

                              {fields.length === 0 && (
                                <div style={{ padding: '24px 0', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
                                  暂无数据，点击右上角"添加"新增
                                </div>
                              )}

                              {fields.map(({ key, name: fieldName, ...restField }, index) => (
                                <div
                                  key={key}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '40px 1fr 100px 1fr 60px',
                                    gap: 0,
                                    padding: '4px 12px',
                                    alignItems: 'center',
                                    borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
                                    background: isDark
                                      ? (index % 2 === 0 ? 'var(--bg)' : 'var(--card)')
                                      : (index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'),
                                  }}
                                >
                                  <Checkbox defaultChecked />
                                  <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0, marginRight: 8 }}>
                                    <Input size="small" placeholder="参数名称" bordered={false} />
                                  </Form.Item>
                                  <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0, marginRight: 8 }}>
                                    <Select
                                      size="small"
                                      bordered={false}
                                      options={urlencodedTypeOptions}
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                  <Form.Item {...restField} name={[fieldName, 'remark']} style={{ marginBottom: 0, marginRight: 8 }}>
                                    <Input size="small" placeholder="参数值" bordered={false} />
                                  </Form.Item>
                                  <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
                                    删除
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </Form.List>
                      )}
                      {/* json / xml / raw: 文本输入框 */}
                      {(bodyType === 'json' || bodyType === 'xml' || bodyType === 'raw') && (
                        <div style={{ position: 'relative' }}>
                          <Button
                            size="small"
                            style={{ position: 'absolute', right: 0, top: -36, zIndex: 1 }}
                            onClick={() => {
                              const currentBody = form.getFieldValue('body') || ''
                              if (!currentBody.trim()) {
                                message.warning('没有可格式化的内容')
                                return
                              }
                              if (bodyType === 'xml') {
                                // XML 简单格式化：缩进嵌套标签
                                let formatted = currentBody
                                try {
                                  formatted = currentBody.replace(/></g, '>\n<')
                                  const lines = formatted.split('\n')
                                  let indent = 0
                                  formatted = lines.map((line: string) => {
                                    const trimmed = line.trim()
                                    if (!trimmed) return ''
                                    if (trimmed.startsWith('</')) indent = Math.max(0, indent - 1)
                                    const result = '  '.repeat(indent) + trimmed
                                    if (!trimmed.startsWith('</') && !trimmed.endsWith('/>') && !trimmed.startsWith('<?xml')) indent++
                                    return result
                                  }).filter((l: string) => l !== '').join('\n')
                                } catch (e) { console.error('[ApiTest] 操作失败:', e) }
                                form.setFieldsValue({ body: formatted })
                                message.success('已格式化')
                              } else {
                                try {
                                  const parsed = JSON.parse(currentBody)
                                  form.setFieldsValue({ body: JSON.stringify(parsed, null, 2) })
                                  message.success('已格式化')
                                } catch {
                                  message.warning('不是有效的 JSON 格式')
                                }
                              }
                            }}
                          >
                            格式化
                          </Button>
                          <Form.Item name="body" style={{ marginBottom: 0 }}>
                            <Input.TextArea rows={10} placeholder="请输入请求体内容" />
                          </Form.Item>
                        </div>
                      )}
                      {/* binary: 文件上传 */}
                      {bodyType === 'binary' && (
                        <Form.Item name="body" style={{ marginBottom: 0 }}>
                          <Upload.Dragger
                            maxCount={1}
                            beforeUpload={() => false}
                          >
                            <p className="ant-upload-drag-icon">
                              <UploadOutlined style={{ fontSize: 32, color: 'var(--au-primary)' }} />
                            </p>
                            <p style={{ color: 'var(--au-slate-muted)' }}>点击或拖拽文件到此区域上传</p>
                          </Upload.Dragger>
                        </Form.Item>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'query',
                  label: (
                <Tooltip title="地址栏中跟在 ? 后面的参数，如 updateapi?id=1">
                  <span>Query <QuestionCircleOutlined style={{ fontSize: 12 }} /></span>
                </Tooltip>
              ),
                  children: (
                    <div style={{ padding: '0 24px 16px' }}>
                      <Form.List name="paramEntries">
                        {(fields, { add, remove }) => (
                          <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '40px 1fr 100px 1fr 1fr 60px',
                              gap: 0,
                              padding: '8px 12px',
                              background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                              borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                              color: isDark ? 'var(--text-light)' : 'var(--au-slate)',
                              fontWeight: 500,
                              fontSize: 13,
                              alignItems: 'center',
                            }}>
                              <Checkbox disabled defaultChecked />
                              <span>参数名称</span>
                              <span>参数类型</span>
                              <span>参数值</span>
                              <span>描述</span>
                              <Button type="link" size="small" onClick={() => add({ key: '', type: 'string', value: '', remark: '' })} style={{ padding: 0 }}>
                                添加
                              </Button>
                            </div>

                            {fields.length === 0 && (
                              <div style={{ padding: '24px 0', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
                                暂无数据，点击右上角"添加"新增
                              </div>
                            )}

                            {fields.map(({ key, name: fieldName, ...restField }, index) => (
                              <div
                                key={key}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '40px 1fr 100px 1fr 1fr 60px',
                                  gap: 0,
                                  padding: '4px 12px',
                                  alignItems: 'center',
                                  borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
                                  background: isDark
                                    ? (index % 2 === 0 ? 'var(--bg)' : 'var(--card)')
                                    : (index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'),
                                }}
                              >
                                <Checkbox defaultChecked />
                                <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="参数名" bordered={false} />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'type']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Select size="small" bordered={false} options={paramTypeOptions} style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="参数值" bordered={false} />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'remark']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="描述" bordered={false} />
                                </Form.Item>
                                <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
                                  删除
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </Form.List>
                    </div>
                  ),
                },
                { key: 'rest',
                  label: (
                    <Tooltip title="地址栏中被斜杠/分隔的参数，如updateapi/{id}">
                      <span>REST <QuestionCircleOutlined style={{ fontSize: 12 }} /></span>
                    </Tooltip>
                  ),
                  children: (
                    <div style={{ padding: '0 24px 16px' }}>
                      <Form.List name="restEntries">
                        {(fields, { add, remove }) => (
                          <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '40px 1fr 100px 1fr 1fr 60px',
                              gap: 0,
                              padding: '8px 12px',
                              background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                              borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                              color: isDark ? 'var(--text-light)' : 'var(--au-slate)',
                              fontWeight: 500,
                              fontSize: 13,
                              alignItems: 'center',
                            }}>
                              <Checkbox disabled defaultChecked />
                              <span>参数名称</span>
                              <span>参数类型</span>
                              <span>参数值</span>
                              <span>描述</span>
                              <Button type="link" size="small" onClick={() => add({ key: '', type: 'string', value: '', remark: '' })} style={{ padding: 0 }}>
                                添加
                              </Button>
                            </div>

                            {fields.length === 0 && (
                              <div style={{ padding: '24px 0', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
                                暂无数据，点击右上角"添加"新增
                              </div>
                            )}

                            {fields.map(({ key, name: fieldName, ...restField }, index) => (
                              <div
                                key={key}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '40px 1fr 100px 1fr 1fr 60px',
                                  gap: 0,
                                  padding: '4px 12px',
                                  alignItems: 'center',
                                  borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
                                  background: isDark
                                    ? (index % 2 === 0 ? 'var(--bg)' : 'var(--card)')
                                    : (index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'),
                                }}
                              >
                                <Checkbox defaultChecked />
                                <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="参数名" bordered={false} />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'type']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Select size="small" bordered={false} options={paramTypeOptions} style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="参数值" bordered={false} />
                                </Form.Item>
                                <Form.Item {...restField} name={[fieldName, 'remark']} style={{ marginBottom: 0, marginRight: 8 }}>
                                  <Input size="small" placeholder="描述" bordered={false} />
                                </Form.Item>
                                <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
                                  删除
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </Form.List>
                    </div>
                  ),
                },
                { key: 'pre', label: '前置', children: <div style={{ padding: '24px', color: 'var(--au-slate-muted)' }}>前置脚本（待开发）</div> },
                { key: 'post', label: '后置', children: <div style={{ padding: '24px', color: 'var(--au-slate-muted)' }}>后置脚本（待开发）</div> },
                { key: 'assert', label: '断言规则', children: (
                  <div style={{ padding: '0 24px 16px' }}>
                    <Dropdown
                      menu={{
                        items: assertionTypeOptions.map((opt) => ({
                          key: opt.value,
                          label: opt.label,
                          onClick: () => addAssertionRule(opt.value as AssertionRule['type']),
                        })),
                      }}
                    >
                      <Button type="primary" style={{ marginBottom: 12 }}>
                        添加断言规则
                      </Button>
                    </Dropdown>
                    {assertionRules.length === 0 && (
                      <div style={{ padding: '24px 0', color: 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center' }}>
                        暂无断言规则，点击上方按钮添加
                      </div>
                    )}
                    {assertionRules.map((rule) => (
                      <div
                        key={rule.id}
                        style={{
                          border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
                          borderRadius: 4,
                          padding: '6px 10px',
                          marginBottom: 6,
                          background: isDark ? 'var(--bg)' : 'var(--au-header-bg)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Tag color="blue" style={{ margin: 0, flexShrink: 0 }}>{assertionTypeOptions.find((o) => o.value === rule.type)?.label}</Tag>
                          {/* 响应头：Header Key + 判断 + 预期值 */}
                          {rule.type === 'response_header' && (
                            <AutoComplete
                              size="small"
                              placeholder="Header Key"
                              value={rule.key}
                              onChange={(v) => updateAssertionRule(rule.id, { key: v })}
                              style={{ width: 140 }}
                              options={commonHeaders}
                              filterOption={(inputValue, option) =>
                                option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                              }
                            />
                          )}
                          {/* 响应体：表达式 + 判断 + 预期值 */}
                          {rule.type === 'response_body' && (
                            <Input
                              size="small"
                              placeholder="表达式(选填)"
                              value={rule.expression}
                              onChange={(e) => updateAssertionRule(rule.id, { expression: e.target.value })}
                              style={{ width: 130 }}
                            />
                          )}
                          <Select
                            size="small"
                            value={rule.operator}
                            onChange={(v) => updateAssertionRule(rule.id, { operator: v })}
                            style={{ width: 100 }}
                            options={assertionOperatorMap[rule.type] || []}
                          />
                          {!['is_empty', 'is_not_empty', 'is_null', 'is_not_null', 'field_exists', 'field_not_exists'].includes(rule.operator) && (
                            <Input
                              size="small"
                              placeholder={
                                rule.type === 'response_time' ? '毫秒数' :
                                rule.operator === 'regex_match' ? '正则表达式' :
                                rule.operator === 'type_is' ? '类型名' :
                                rule.operator?.startsWith('length') ? '长度值' :
                                rule.operator?.startsWith('greater') || rule.operator?.startsWith('less') ? '数值' :
                                '预期值'
                              }
                              value={rule.expected}
                              onChange={(e) => updateAssertionRule(rule.id, { expected: e.target.value })}
                              style={{ flex: 1 }}
                            />
                          )}
                          <Button
                            type="text"
                            size="small"
                            danger
                            onClick={() => removeAssertionRule(rule.id)}
                            style={{ padding: '0 4px', flexShrink: 0 }}
                          >
                            删除
                          </Button>
                        </div>
                        {/* 断言行结果小标签 */}
                        {assertionResults.find((r) => r.id === rule.id) && (
                          <div style={{ marginTop: 4 }}>
                            {(() => {
                              const result = assertionResults.find((r) => r.id === rule.id)!
                              return (
                                <Tag color={result.passed ? 'success' : 'error'} style={{ fontSize: 12 }}>
                                  {result.passed ? '通过' : '失败'}: {result.message}
                                </Tag>
                              )
                            })()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) },
                { key: 'settings', label: '设置', children: <div style={{ padding: '24px', color: 'var(--au-slate-muted)' }}>高级设置（待开发）</div> },
              ]}
            />
          </div>

          {/* 响应内容：下半部分 */}
          <div style={{
            borderTop: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)',
            flex: '1 1 250px',
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
            background: isDark ? 'var(--bg)' : '#ffffff',
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 24px',
              borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
              gap: 16,
              flexShrink: 0,
            }}>
              <Text style={{ fontWeight: 600, fontSize: 14, color: isDark ? 'var(--text)' : 'var(--au-slate)' }}>响应内容</Text>
              <Space size={4}>
                <Text
                  style={{
                    cursor: 'pointer',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 13,
                    color: responseTab === 'body' ? 'var(--au-primary)' : 'var(--au-slate-muted)',
                    background: responseTab === 'body' ? 'var(--au-primary-50)' : 'transparent',
                  }}
                  onClick={() => setResponseTab('body')}
                >
                  Body
                </Text>
                <Text
                  style={{
                    cursor: 'pointer',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 13,
                    color: responseTab === 'headers' ? 'var(--au-primary)' : 'var(--au-slate-muted)',
                    background: responseTab === 'headers' ? 'var(--au-primary-50)' : 'transparent',
                  }}
                  onClick={() => setResponseTab('headers')}
                >
                  Headers
                </Text>
                <Text
                  style={{
                    cursor: 'pointer',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 13,
                    color: responseTab === 'assert' ? 'var(--au-primary)' : 'var(--au-slate-muted)',
                    background: responseTab === 'assert' ? 'var(--au-primary-50)' : 'transparent',
                  }}
                  onClick={() => setResponseTab('assert')}
                >
                  断言结果
                </Text>
              </Space>
              {responseResult && (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, fontSize: 13 }}>
                  <span>
                    <span style={{ color: 'var(--au-slate-muted)' }}>Status: </span>
                    <span style={{ color: responseResult.status < 400 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
                      {responseResult.status}
                    </span>
                  </span>
                  <span>
                    <span style={{ color: 'var(--au-slate-muted)' }}>Time: </span>
                    <span style={{ color: 'var(--au-slate)', fontWeight: 600 }}>
                      {responseResult.elapsed_ms}ms
                    </span>
                  </span>
                  <span>
                    <span style={{ color: 'var(--au-slate-muted)' }}>Result: </span>
                    <span style={{ color: responseResult.status < 400 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                      {responseResult.status < 400 ? '成功' : '失败'}
                    </span>
                  </span>
                </div>
              )}
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '12px 24px' }}>
              {(() => {
                if (!responseResult) {
                  return (
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--au-text-faint)' }}>
                      <Empty
                        description={
                          <span>
                            点击 <span style={{ color: 'var(--au-primary)', cursor: 'pointer' }} onClick={handleDebug}>发送</span> 获取响应内容
                          </span>
                        }
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    </div>
                  )
                }
                if (responseTab === 'body') {
                  return (
                    <div style={{ position: 'relative' }}>
                      <Space style={{ position: 'absolute', right: 0, top: -4, zIndex: 1 }}>
                        <Button size="small" onClick={handleFormatResponse}>格式化</Button>
                        <Button size="small" onClick={() => { navigator.clipboard.writeText(responseResult.body); message.success('已复制') }}>复制</Button>
                      </Space>
                      <pre style={{ margin: 0, fontSize: 13, fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace", whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.7, background: isDark ? 'var(--bg)' : 'var(--au-header-bg)', padding: '12px 16px', borderRadius: 6, border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', maxHeight: '100%', overflow: 'auto', color: isDark ? 'var(--text)' : undefined }}>
                        {highlightJson(responseResult.body)}
                      </pre>
                    </div>
                  )
                }
                if (responseTab === 'headers') {
                  return (
                    <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 6, overflow: 'hidden', fontSize: 13 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', padding: '8px 16px', background: isDark ? 'var(--bg)' : 'var(--au-row-alt)', borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', fontWeight: 600, color: isDark ? 'var(--text-light)' : 'var(--au-slate)' }}>
                        <span>Header</span>
                        <span>Value</span>
                      </div>
                      {Object.entries(responseResult.headers).map(([k, v], idx) => (
                        <div key={k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', padding: '6px 16px', background: isDark ? (idx % 2 === 0 ? 'var(--bg)' : 'var(--card)') : (idx % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'), borderBottom: idx < Object.entries(responseResult.headers).length - 1 ? (isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)') : 'none' }}>
                          <span style={{ color: isDark ? '#A5B4FC' : '#0369a1', fontWeight: 500, fontFamily: 'monospace' }}>{k}</span>
                          <span style={{ color: isDark ? 'var(--text)' : 'var(--au-slate)', wordBreak: 'break-all', fontFamily: 'monospace' }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  )
                }
                return (
                  <div>
                    {assertionResults.length === 0 ? (
                      <div style={{ padding: '40px 0', textAlign: 'center', color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)', fontSize: 14 }}>
                        暂无断言结果，请先在"断言规则"Tab中添加规则并发送请求
                      </div>
                    ) : (
                      <div style={{ border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 6, overflow: 'hidden', fontSize: 13 }}>
                        <div style={{ display: 'flex', padding: '8px 16px', background: isDark ? 'var(--bg)' : 'var(--au-row-alt)', borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', fontWeight: 600, color: isDark ? 'var(--text-light)' : 'var(--au-slate)', gap: 16 }}>
                          <span style={{ width: 60 }}>结果</span>
                          <span style={{ flex: 1 }}>断言详情</span>
                        </div>
                        {assertionResults.map((result, idx) => {
                          const rule = assertionRules.find((r) => r.id === result.id)
                          const typeLabel = rule ? assertionTypeOptions.find((o) => o.value === rule.type)?.label : ''
                          return (
                            <div key={result.id} style={{ display: 'flex', padding: '8px 16px', background: isDark ? (idx % 2 === 0 ? 'var(--bg)' : 'var(--card)') : (idx % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)'), borderBottom: idx < assertionResults.length - 1 ? (isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)') : 'none', alignItems: 'center', gap: 16 }}>
                              <Tag color={result.passed ? 'success' : 'error'} style={{ width: 60, textAlign: 'center', margin: 0 }}>
                                {result.passed ? '通过' : '失败'}
                              </Tag>
                              <span style={{ flex: 1, color: isDark ? 'var(--text)' : 'var(--au-slate)' }}>
                                {typeLabel && <Tag style={{ marginRight: 6 }}>{typeLabel}</Tag>}
                                {result.message}
                              </span>
                            </div>
                          )
                        })}
                        <div style={{ padding: '8px 16px', background: isDark ? 'var(--bg)' : 'var(--au-header-bg)', borderTop: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', fontWeight: 600, color: isDark ? 'var(--text-light)' : 'var(--au-slate)' }}>
                          {assertionResults.filter((r) => r.passed).length}/{assertionResults.length} 通过
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </Form>
      </Drawer>

      {/* 保存弹框 */}
      <Modal
        title="保存接口"
        open={saveModalOpen}
        onOk={handleModalConfirm}
        onCancel={() => setSaveModalOpen(false)}
        okText="确定"
        cancelText="取消"
        wrapClassName="aurora-modal-wrap"
        className="aurora-modal"
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>
          <span style={{ color: '#ef4444', marginRight: 4 }}>*</span>
          接口名称
        </div>
        <Input
          placeholder="请输入接口名称"
          value={saveName}
          onChange={(e) => {
            setSaveName(e.target.value)
            if (saveNameError) setSaveNameError('')
          }}
          onPressEnter={handleModalConfirm}
          status={saveNameError ? 'error' : undefined}
        />
        {saveNameError && (
          <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{saveNameError}</div>
        )}
        <div style={{ marginTop: 16, marginBottom: 8 }}>接口描述</div>
        <Input.TextArea
          placeholder="请输入接口描述（选填）"
          value={saveDescription}
          onChange={(e) => setSaveDescription(e.target.value)}
          rows={3}
        />
        <div style={{ marginTop: 16, marginBottom: 8 }}>接口状态</div>
        <Select
          value={saveStatus}
          onChange={setSaveStatus}
          style={{ width: '100%' }}
          options={[
            { value: 'active', label: '启用' },
            { value: 'deprecated', label: '废弃' },
          ]}
        />
      </Modal>

      {/* 批量导入弹框 */}
      <Modal
        title="批量导入接口"
        open={batchImportOpen}
        onCancel={() => setBatchImportOpen(false)}
        onOk={handleBatchImportSubmit}
        okText="导入"
        cancelText="取消"
        wrapClassName="aurora-modal-wrap"
        className="aurora-modal"
        confirmLoading={importing}
        destroyOnClose
        width={720}
      >
        <div style={{ marginBottom: 16, color: isDark ? 'var(--text)' : undefined }}>
          支持导入 cURL 命令、Postman Collection（JSON）、Swagger 2.0/OpenAPI 3.x（YAML）三种格式。
        </div>
        <Radio.Group
          value={importType}
          onChange={(e) => { setImportType(e.target.value); setImportResult(null) }}
          style={{ marginBottom: 16 }}
        >
          <Radio value="curl">cURL</Radio>
          <Radio value="postman">Postman</Radio>
          <Radio value="swagger">Swagger 2.0/OpenAPI 3.x</Radio>
        </Radio.Group>

        {importType === 'curl' && (
          <div>
            <div style={{ marginBottom: 8, color: isDark ? 'var(--text)' : undefined }}>请粘贴 cURL 命令：</div>
            <Input.TextArea
              rows={8}
              placeholder="curl -X POST https://api.example.com/users -H 'Content-Type: application/json' -d '{&quot;name&quot;:&quot;test&quot;}'"
              value={curlText}
              onChange={(e) => setCurlText(e.target.value)}
              style={{ fontFamily: 'monospace' }}
            />
          </div>
        )}

        {importType === 'postman' && (
          <div>
            <div style={{ marginBottom: 8, color: isDark ? 'var(--text)' : undefined }}>请上传 Postman Collection JSON 文件：</div>
            <Upload.Dragger
              accept=".json"
              maxCount={1}
              beforeUpload={() => false}
              onChange={handleImportFileChange}
              showUploadList={false}
            >
              <p style={{ color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)' }}>
                {importFile ? importFile.name : '点击或拖拽文件到此区域上传'}
              </p>
            </Upload.Dragger>
          </div>
        )}

        {importType === 'swagger' && (
          <div>
            <div style={{ marginBottom: 8, color: isDark ? 'var(--text)' : undefined }}>请上传 Swagger 2.0/OpenAPI 3.x YAML 文件：</div>
            <Upload.Dragger
              accept=".yaml,.yml"
              maxCount={1}
              beforeUpload={() => false}
              onChange={handleImportFileChange}
              showUploadList={false}
            >
              <p style={{ color: isDark ? 'var(--text-lighter)' : 'var(--au-slate-muted)' }}>
                {importFile ? importFile.name : '点击或拖拽文件到此区域上传'}
              </p>
            </Upload.Dragger>
          </div>
        )}

        {importResult && (
          <div style={{ marginTop: 16, padding: 12, border: isDark ? '1px solid var(--border)' : '1px solid var(--au-border)', borderRadius: 6, background: isDark ? 'var(--bg)' : 'var(--au-header-bg)' }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: isDark ? 'var(--text)' : undefined }}>
              导入结果：共 {importResult.total} 条，成功 {importResult.success_count} 条，失败 {importResult.fail_count} 条
            </div>
            {importResult.items.length > 0 && (
              <div style={{ maxHeight: 240, overflow: 'auto' }}>
                {importResult.items.map((item: apiDefinitionApi.ApiImportItem, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '4px 8px',
                      borderBottom: isDark ? '1px solid var(--border)' : '1px solid var(--au-row-alt)',
                      color: isDark ? 'var(--text)' : undefined,
                    }}
                  >
                    <span style={{ flex: 1 }}>{item.index + 1}. {item.name || '(未命名)'}</span>
                    <Tag color={item.success ? 'success' : 'error'} style={{ margin: 0 }}>
                      {item.success ? '成功' : '失败'}
                    </Tag>
                    {!item.success && item.reason && (
                      <span style={{ marginLeft: 8, color: '#ef4444', fontSize: 12 }}>{item.reason}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
